import { useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import LogsIcon from '../../../assets/logs-icon.svg';
import { RecentActivity as RecentActivityType } from '../../../types/Dashboard.types';
import { formatEventType } from '../../../utils/EventFormatting.util';
import { AttentionMessage } from '../../ui/AttentionMessage/AttentionMessage';
import { Divider } from '../../ui/Divider/Divider';
import { EmptyState } from '../../ui/EmptyState/EmptyState';

type RecentActivityProps = {
  activities: RecentActivityType[];
};

type SeverityLevel = 'major' | 'moderate' | 'minor';

type ActivityItem = {
  id: string;
  aiSystemId: string;
  aiSystemName: string;
  repositoryName: string;
  description?: string;
  date: string;
  severity: SeverityLevel;
  eventType: string | null;
};

const severityStyles: Record<
  SeverityLevel,
  { bg: string; text: string; label: string }
> = {
  major: {
    bg: 'bg-rose-100',
    text: 'text-rose-700',
    label: 'Major'
  },
  moderate: {
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    label: 'Moderate'
  },
  minor: {
    bg: 'bg-cyan-100',
    text: 'text-cyan-700',
    label: 'Minor'
  }
};

function ActivityItemComponent({
  item,
  isLast
}: {
  item: ActivityItem;
  isLast: boolean;
}) {
  const severity = severityStyles[item.severity];
  const formattedEventType = formatEventType(item.eventType);
  const [showEventTypeTooltip, setShowEventTypeTooltip] = useState(false);
  const [eventTypeTooltipPosition, setEventTypeTooltipPosition] = useState({
    x: 0,
    y: 0
  });

  const handleEventTypeMouseMove = (event: MouseEvent<HTMLSpanElement>) => {
    setEventTypeTooltipPosition({
      x: event.clientX + 10,
      y: event.clientY + 10
    });
  };

  return (
    <>
      <Link
        to={`/ai-systems/${item.aiSystemId}/monitoring-audit-logging`}
        state={{ openEventId: item.id }}
        className="flex w-full min-w-0 cursor-pointer flex-col gap-1 rounded-md p-1 hover:bg-gray-50"
      >
        <div className="flex min-w-0 items-start justify-between gap-3">
          <h4 className="flex min-w-0 flex-1 items-center overflow-hidden font-inter text-base font-medium leading-6 text-gray-900">
            <span className="block min-w-0 truncate">{item.aiSystemName}</span>
            <span className="ml-1 mr-2">•</span>
            <span
              className="block min-w-0 flex-1 truncate text-left"
              onMouseEnter={() => setShowEventTypeTooltip(true)}
              onMouseLeave={() => setShowEventTypeTooltip(false)}
              onMouseMove={handleEventTypeMouseMove}
            >
              {formattedEventType}
            </span>
            {showEventTypeTooltip && (
              <div
                className="fixed z-50 max-w-sm rounded border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 shadow-lg"
                style={{
                  left: `${eventTypeTooltipPosition.x}px`,
                  top: `${eventTypeTooltipPosition.y}px`
                }}
              >
                {formattedEventType}
              </div>
            )}
          </h4>
          <span
            className={`${severity.bg} ${severity.text} inline-flex h-5 shrink-0 items-center justify-center rounded-[10px] px-[10px] py-[2px] text-center font-inter text-xs font-medium leading-4`}
          >
            {severity.label}
          </span>
        </div>
        <div className="flex min-w-0 items-center gap-2">
          <span className="block min-w-0 truncate font-inter text-sm font-normal leading-5 text-gray-800">
            {item.repositoryName} • {item.date}
          </span>
        </div>
        {severity.label === 'Major' && (
          <div>
            <AttentionMessage />
          </div>
        )}
      </Link>
      {!isLast && (
        <div className="flex w-full items-center py-[14px]">
          <Divider classes="w-full" />
        </div>
      )}
    </>
  );
}

export function RecentActivity({ activities }: RecentActivityProps) {
  const displayActivities: ActivityItem[] = activities.map((activity) => ({
    id: activity.id,
    aiSystemId: activity.aiSystemId,
    aiSystemName: activity.aiSystemName,
    repositoryName: `${activity.repositoryName}`,
    description: activity.eventDescription,
    date: new Date(activity.completedAt).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    severity: (activity.severity?.toLowerCase() || 'minor') as SeverityLevel,
    eventType: activity.eventType
  }));

  if (displayActivities.length === 0) {
    return (
      <div className="flex h-full min-w-0 flex-col gap-4 rounded-lg bg-white p-6 shadow-sm ring-1 ring-gray-250v2">
        <h3 className="font-inter font-medium text-xs leading-4 tracking-[0.05em] uppercase text-gray-550 text-left flex-shrink-0">
          RECENT ACTIVITY
        </h3>
        <EmptyState
          icon={
            <img src={LogsIcon} alt="No logs" className="w-[28px] h-[36px]" />
          }
          title="No Logs yet"
          description="Activities will appear here once recorded."
          classes="!p-0 !border-0 !shadow-none flex-1"
        />
      </div>
    );
  }

  return (
    <div className="flex max-h-[608px] min-w-0 flex-col gap-4 rounded-lg bg-white p-6 shadow-sm ring-1 ring-gray-250v2">
      <h3 className="font-inter font-medium text-xs leading-4 tracking-[0.05em] uppercase text-gray-550 text-left flex-shrink-0">
        RECENT ACTIVITY
      </h3>
      <div className="flex flex-1 flex-col overflow-x-hidden overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        {displayActivities.map((activity, index) => (
          <ActivityItemComponent
            key={activity.id}
            item={activity}
            isLast={index === displayActivities.length - 1}
          />
        ))}
      </div>
    </div>
  );
}
